import { createContext, useCallback, useContext, useRef, type ReactNode, type WheelEvent } from "react";

/**
 * D-15 — the page never scrolls; only the data region does.
 *
 * Every screen inside `AppShell` must be wrapped in `<Page>`. It is a
 * full-height flex column: the header, filters and stat tiles stay put, and
 * exactly one child — the table or list — is allowed to scroll.
 *
 * A page that returns a bare fragment instead will look correct and quietly
 * reintroduce document scroll, which is why this wrapper exists rather than a
 * convention.
 *
 *   <Page>
 *     <PageHeader … />
 *     <StatGrid>…</StatGrid>        ← fixed
 *     <PageBody>                    ← the one scrolling region
 *       <Card fill>…table…</Card>
 *     </PageBody>
 *   </Page>
 */
const BleedContext = createContext(false);
const DocumentContext = createContext(false);

/** True inside a `<Page bleed>` — its table runs edge to edge instead of sitting in a card. */
export function useBleed(): boolean {
  return useContext(BleedContext);
}

/** Marks the one region `Page` should send stray wheel events to. */
const SCROLL_ATTR = "data-page-scroll";

function isScrollable(el: Element): boolean {
  const style = getComputedStyle(el);
  const overflow = `${style.overflowY} ${style.overflowX}`;
  return /auto|scroll/.test(overflow) && el.scrollHeight > el.clientHeight + 1;
}

/**
 * Does this wheel event already have somewhere to go?
 *
 * Walks up from the cursor to the page root. If anything on the way is a live
 * scroll container — the page body, a capped list, a table — the browser is
 * already doing the right thing and we must not scroll a second box as well.
 */
function hasOwnScroller(target: Element | null, root: Element): boolean {
  for (let el = target; el && el !== root; el = el.parentElement) {
    if (isScrollable(el)) return true;
  }
  return false;
}

/** The region `Page` forwards to: the marked one, else the first live scroller. */
function findScroller(root: Element): HTMLElement | null {
  const marked = root.querySelector<HTMLElement>(`[${SCROLL_ATTR}]`);
  if (marked) return marked;
  for (const el of root.querySelectorAll<HTMLElement>("*")) {
    if (isScrollable(el)) return el;
  }
  return null;
}

export function Page({
  children,
  className = "",
  bleed = false,
  fullWidth = false,
  layout = "workspace",
}: {
  children: ReactNode;
  className?: string;
  /**
   * A list page: its table uses the whole content area, edge to edge, with
   * no card around it and no padding outside it. `AppShell` drops its own
   * padding and width cap when it holds one (`data-bleed`).
   */
  bleed?: boolean;
  fullWidth?: boolean;
  /** Long dashboards scroll at the workspace edge instead of inside a panel. */
  layout?: "workspace" | "document";
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  /*
   * D-15 pins the header, filters and stat tiles, which leaves them dead to
   * the mouse wheel: on a screen with a tall filter bar — the performance
   * tracker most of all — the cursor usually rests on chrome, and scrolling
   * there did nothing at all. The fix is not to unpin the chrome, which is
   * the whole point of the rule, but to send its wheel events to the one
   * region that does scroll.
   *
   * Nothing is forwarded when the cursor is already over a scroll container,
   * so a capped list or a wide table still scrolls itself and still chains to
   * the page when it runs out — both native behaviours, left alone.
   *
   * `preventDefault` is neither needed nor possible: React attaches wheel
   * listeners passively, and `body { overflow: hidden }` means there is no
   * document scroll to suppress in the first place.
   */
  const onWheel = useCallback((e: WheelEvent<HTMLDivElement>) => {
    if (layout === "document") return;
    const root = rootRef.current;
    if (!root || e.deltaY === 0) return;
    if (hasOwnScroller(e.target as Element | null, root)) return;

    const scroller = findScroller(root);
    if (!scroller) return;

    // deltaMode: 0 pixels, 1 lines, 2 pages.
    const step =
      e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * scroller.clientHeight : e.deltaY;
    scroller.scrollTop += step;
  }, [layout]);

  return (
    <BleedContext.Provider value={bleed}>
      <DocumentContext.Provider value={layout === "document"}>
      <div
        className={`gq-stagger flex min-w-0 flex-col ${layout === "document" ? "min-h-full" : "h-full min-h-0"} ${className}`}
        data-bleed={bleed || undefined}
        data-full-width={fullWidth || undefined}
        data-document-page={layout === "document" || undefined}
        onWheel={onWheel}
        ref={rootRef}
      >
        {children}
      </div>
      </DocumentContext.Provider>
    </BleedContext.Provider>
  );
}

/**
 * The single scrolling region of a page.
 *
 * `min-h-0` is not decoration: a flex child defaults to `min-height: auto`,
 * so without it this box refuses to shrink below its content, grows instead
 * of scrolling, and pushes the page into a document scroll. Every flex
 * ancestor between the shell and here carries it too.
 */
export function PageBody({ children, className = "" }: { children: ReactNode; className?: string }) {
  const document = useContext(DocumentContext);
  return (
    <div className={`${document ? "min-w-0 flex-none" : "min-h-0 flex-1 overflow-y-auto"} ${className}`} {...(document ? {} : { [SCROLL_ATTR]: "" })}>
      {children}
    </div>
  );
}

/**
 * A page whose content is a single table that should fill the remaining
 * height, with only its rows scrolling — the common case for every list
 * screen in the app.
 */
export function PageFill({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`flex min-h-0 flex-1 flex-col ${className}`}>{children}</div>;
}

/**
 * Marks a screen as one that MAY scroll normally — public sign-in and
 * registration pages, which are documents rather than a workspace and have to
 * work on a small phone. Rendered as an invisible marker the global CSS reads.
 */
export function ScrollablePage({ children }: { children: ReactNode }) {
  return (
    <>
      <span aria-hidden="true" data-scrollable-page="" hidden />
      {children}
    </>
  );
}
